// Country-specific themes for subdomain-based styling
// Each country gets unique colors, gradients, and cultural identity

export const COUNTRY_THEMES = {
  usa: {
    subdomain: 'us',
    tagline: 'Professional Lease Agreements for All 50 States',
    legalBadge: 'State & Federal Compliant',
    colors: {
      primary: '#1B3A5C',
      primaryLight: '#2A5A8C',
      secondary: '#C41E3A',
      secondaryLight: '#E8334F',
      accent: '#D4AF37',
      heroBg: 'linear-gradient(135deg, #1B3A5C 0%, #2A5A8C 40%, #1B3A5C 70%, #C41E3A 100%)',
      buttonBg: 'linear-gradient(135deg, #1B3A5C 0%, #2A5A8C 100%)',
      buttonHover: 'linear-gradient(135deg, #142D4A 0%, #1F4A75 100%)',
      cardAccent: '#1B3A5C',
      footerBg: '#0F2238',
    },
    pattern: 'stars',
    local: {
      productName: 'Residential Lease Agreement',
      heroTitle: 'Legally Binding Lease Agreements',
      heroSubtitle: 'State-specific rental contracts for California, Texas, New York and every other state. Drafted to meet your state\'s exact requirements.',
      lawRef: 'Landlord-Tenant Law · State Statutes',
      lawVerified: 'Reviewed by licensed U.S. attorneys',
      priceComparison: 'vs. $300–$500 with a real estate attorney',
      cities: ['Los Angeles', 'New York', 'Houston', 'Chicago'],
      disputeBody: 'Local Housing Court',
      currencyNote: 'Priced in USD',
      trustLine: 'Compliant with federal Fair Housing Act and all state landlord-tenant statutes',
    },
  },
  uk: {
    subdomain: 'uk',
    tagline: 'Tenancy Agreements Compliant with UK Housing Law',
    legalBadge: 'Housing Act Compliant',
    colors: {
      primary: '#1B2A4A',
      primaryLight: '#2B3F6B',
      secondary: '#C5A44E',
      secondaryLight: '#D4B65E',
      accent: '#C5A44E',
      heroBg: 'linear-gradient(135deg, #1B2A4A 0%, #2B3F6B 50%, #1B2A4A 80%, #C5A44E 100%)',
      buttonBg: 'linear-gradient(135deg, #1B2A4A 0%, #2B3F6B 100%)',
      buttonHover: 'linear-gradient(135deg, #131F38 0%, #1F2F55 100%)',
      cardAccent: '#1B2A4A',
      footerBg: '#0E1829',
    },
    pattern: 'crosshatch',
    local: {
      productName: 'Assured Shorthold Tenancy Agreement',
      heroTitle: 'AST Tenancy Agreements Done in Minutes',
      heroSubtitle: 'Housing Act 1988 compliant tenancy agreements for England, Wales, Scotland and Northern Ireland. Includes deposit protection, Right to Rent and all prescribed information.',
      lawRef: 'Housing Act 1988 · Tenant Fees Act 2019',
      lawVerified: 'Reviewed by UK property solicitors',
      priceComparison: 'vs. £200–£400 with a solicitor',
      cities: ['London', 'Manchester', 'Birmingham', 'Edinburgh'],
      disputeBody: 'First-tier Tribunal (Property Chamber)',
      currencyNote: 'Priced in GBP',
      trustLine: 'Fully compliant with the Housing Act 1988 and the Renters Reform Bill requirements',
    },
  },
  canada: {
    subdomain: 'ca',
    tagline: 'Lease Agreements for Every Province & Territory',
    legalBadge: 'Provincial Law Compliant',
    colors: {
      primary: '#C41E3A',
      primaryLight: '#E8334F',
      secondary: '#FFFFFF',
      secondaryLight: '#F5F5F5',
      accent: '#C41E3A',
      heroBg: 'linear-gradient(135deg, #C41E3A 0%, #E8334F 40%, #C41E3A 70%, #8B1528 100%)',
      buttonBg: 'linear-gradient(135deg, #C41E3A 0%, #E8334F 100%)',
      buttonHover: 'linear-gradient(135deg, #A0182F 0%, #C41E3A 100%)',
      cardAccent: '#C41E3A',
      footerBg: '#2D0A10',
    },
    pattern: 'maple',
    local: {
      productName: 'Residential Tenancy Agreement',
      heroTitle: 'Residential Lease Agreements for Every Province',
      heroSubtitle: 'Province-specific tenancy agreements for Ontario, Quebec, BC, Alberta and all territories. Meets the exact requirements of your provincial Residential Tenancies Act.',
      lawRef: 'Residential Tenancies Act · Provincial Standards',
      lawVerified: 'Reviewed by Canadian property lawyers',
      priceComparison: 'vs. $250–$500 CAD with a property manager',
      cities: ['Toronto', 'Montréal', 'Vancouver', 'Calgary'],
      disputeBody: 'Landlord and Tenant Board',
      currencyNote: 'Priced in CAD',
      trustLine: 'Compliant with the RTA (Ontario), RLRQ (Québec) and provincial tenancy legislation across Canada',
    },
  },
  france: {
    subdomain: 'fr',
    tagline: 'Contrats de Location Conformes au Droit Français',
    legalBadge: 'Loi ALUR Conforme',
    colors: {
      primary: '#002654',
      primaryLight: '#003D7A',
      secondary: '#C41E3A',
      secondaryLight: '#E8334F',
      accent: '#C5A44E',
      heroBg: 'linear-gradient(135deg, #002654 0%, #003D7A 40%, #002654 70%, #C41E3A15 100%)',
      buttonBg: 'linear-gradient(135deg, #002654 0%, #003D7A 100%)',
      buttonHover: 'linear-gradient(135deg, #001A3D 0%, #002B5C 100%)',
      cardAccent: '#002654',
      footerBg: '#001229',
    },
    pattern: 'fleurdelis',
    local: {
      productName: 'Bail d\'Habitation',
      heroTitle: 'Votre Bail d\'Habitation en 15 Minutes',
      heroSubtitle: 'Contrat de location conforme à la loi ALUR du 6 juillet 1989. Meublé ou vide, avec diagnostics DDT, clause IRL et toutes les mentions légales obligatoires.',
      lawRef: 'Loi n° 89-462 du 6 juillet 1989 · Loi ALUR',
      lawVerified: 'Validé par des juristes en droit immobilier français',
      priceComparison: 'vs. 150 à 400 € chez un notaire',
      cities: ['Paris', 'Lyon', 'Marseille', 'Bordeaux'],
      disputeBody: 'Tribunal judiciaire compétent',
      currencyNote: 'Tarif en euros',
      trustLine: 'Conforme à la loi du 6 juillet 1989 et aux obligations ALUR — diagnostics DDT intégrés',
    },
  },
  germany: {
    subdomain: 'de',
    tagline: 'Mietverträge nach Deutschem Recht',
    legalBadge: 'BGB-Konform',
    colors: {
      primary: '#2C2C2C',
      primaryLight: '#3D3D3D',
      secondary: '#C5A44E',
      secondaryLight: '#D4B65E',
      accent: '#C5A44E',
      heroBg: 'linear-gradient(135deg, #2C2C2C 0%, #3D3D3D 40%, #2C2C2C 70%, #C5A44E 100%)',
      buttonBg: 'linear-gradient(135deg, #2C2C2C 0%, #3D3D3D 100%)',
      buttonHover: 'linear-gradient(135deg, #1A1A1A 0%, #2C2C2C 100%)',
      cardAccent: '#2C2C2C',
      footerBg: '#1A1A1A',
    },
    pattern: 'geometric',
    local: {
      productName: 'Mietvertrag',
      heroTitle: 'Rechtssicherer Mietvertrag nach BGB',
      heroSubtitle: 'Mietverträge nach § 535 BGB für ganz Deutschland. Mit Mietpreisbremse, Schönheitsreparaturklauseln, Nebenkosten und allen gesetzlich vorgeschriebenen Angaben.',
      lawRef: '§ 535 BGB · Mietrechtsreformgesetz',
      lawVerified: 'Von deutschen Fachanwälten für Mietrecht geprüft',
      priceComparison: 'vs. 150–350 € beim Rechtsanwalt',
      cities: ['Berlin', 'München', 'Hamburg', 'Köln'],
      disputeBody: 'Amtsgericht (Mietgericht)',
      currencyNote: 'Preise in Euro',
      trustLine: 'Konform mit dem BGB, Mietrechtsreformgesetz und länderspezifischen Regelungen zur Mietpreisbremse',
    },
  },
  italy: {
    subdomain: 'it',
    tagline: 'Contratti di Locazione Conformi alla Legge Italiana',
    legalBadge: 'Codice Civile Conforme',
    colors: {
      primary: '#1B5E20',
      primaryLight: '#2E7D32',
      secondary: '#BF6340',
      secondaryLight: '#D4795A',
      accent: '#BF6340',
      heroBg: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 40%, #1B5E20 70%, #BF6340 100%)',
      buttonBg: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
      buttonHover: 'linear-gradient(135deg, #134517 0%, #1B5E20 100%)',
      cardAccent: '#1B5E20',
      footerBg: '#0E2E10',
    },
    pattern: 'arches',
    local: {
      productName: 'Contratto di Locazione',
      heroTitle: 'Contratto di Locazione in 15 Minuti',
      heroSubtitle: 'Contratti 4+4 e 3+2 conformi alla Legge 431/98 e al Codice Civile. Con aggiornamento ISTAT, cedolare secca e tutte le clausole obbligatorie per legge.',
      lawRef: 'Legge n. 431/1998 · Codice Civile art. 1571',
      lawVerified: 'Redatto da avvocati specializzati in diritto immobiliare italiano',
      priceComparison: 'vs. 200–500 € dal notaio o agenzia',
      cities: ['Roma', 'Milano', 'Napoli', 'Torino'],
      disputeBody: 'Tribunale civile competente',
      currencyNote: 'Prezzi in euro',
      trustLine: 'Conforme alla Legge 431/98, al Codice Civile e alle normative regionali sulla locazione',
    },
  },
  spain: {
    subdomain: 'es',
    tagline: 'Contratos de Alquiler Conformes con la Ley Española',
    legalBadge: 'Ley de Arrendamientos',
    colors: {
      primary: '#C41E3A',
      primaryLight: '#E8334F',
      secondary: '#C5A44E',
      secondaryLight: '#D4B65E',
      accent: '#C5A44E',
      heroBg: 'linear-gradient(135deg, #C41E3A 0%, #E8334F 30%, #C41E3A 60%, #C5A44E 100%)',
      buttonBg: 'linear-gradient(135deg, #C41E3A 0%, #E8334F 100%)',
      buttonHover: 'linear-gradient(135deg, #A0182F 0%, #C41E3A 100%)',
      cardAccent: '#C41E3A',
      footerBg: '#2D0A10',
    },
    pattern: 'mosaic',
    local: {
      productName: 'Contrato de Arrendamiento',
      heroTitle: 'Contrato de Alquiler en 15 Minutos',
      heroSubtitle: 'Contratos de arrendamiento conformes a la LAU y a la Ley de Vivienda 2023. Para vivienda habitual, temporada y habitación — con todas las cláusulas legales obligatorias.',
      lawRef: 'LAU — Ley 29/1994 · Ley de Vivienda 12/2023',
      lawVerified: 'Revisado por abogados especializados en derecho inmobiliario español',
      priceComparison: 'vs. 150–300 € con gestor o abogado',
      cities: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla'],
      disputeBody: 'Juzgado de Primera Instancia',
      currencyNote: 'Precio en euros',
      trustLine: 'Conforme a la LAU (Ley 29/1994) y a la nueva Ley de Vivienda 12/2023, incluyendo zonas tensionadas',
    },
  },
  australia: {
    subdomain: 'au',
    tagline: 'Rental Agreements for Every State & Territory',
    legalBadge: 'Residential Tenancies Act',
    colors: {
      primary: '#006D6F',
      primaryLight: '#008B8D',
      secondary: '#D4870E',
      secondaryLight: '#E89B22',
      accent: '#D4870E',
      heroBg: 'linear-gradient(135deg, #006D6F 0%, #008B8D 40%, #006D6F 70%, #D4870E 100%)',
      buttonBg: 'linear-gradient(135deg, #006D6F 0%, #008B8D 100%)',
      buttonHover: 'linear-gradient(135deg, #005556 0%, #006D6F 100%)',
      cardAccent: '#006D6F',
      footerBg: '#003435',
    },
    pattern: 'waves',
    local: {
      productName: 'Residential Tenancy Agreement',
      heroTitle: 'Residential Tenancy Agreements for Every State',
      heroSubtitle: 'State-specific rental agreements for NSW, VIC, QLD, WA, SA and all territories. Bond lodgement, condition reports and Fair Trading compliant — done in minutes.',
      lawRef: 'Residential Tenancies Act · Fair Trading',
      lawVerified: 'Reviewed by Australian property lawyers',
      priceComparison: 'vs. $200–$400 AUD through a property manager',
      cities: ['Sydney', 'Melbourne', 'Brisbane', 'Perth'],
      disputeBody: 'State Civil & Administrative Tribunal',
      currencyNote: 'Priced in AUD',
      trustLine: 'Compliant with the Residential Tenancies Act in each state and territory — NSW Fair Trading, Consumer Affairs VIC, RTA QLD',
    },
  },
};

// Map subdomain to country code
export const SUBDOMAIN_TO_COUNTRY = Object.entries(COUNTRY_THEMES).reduce(
  (map, [countryCode, theme]) => {
    map[theme.subdomain] = countryCode;
    return map;
  },
  {}
);

// Get theme by country code
export const getCountryTheme = (countryCode) => COUNTRY_THEMES[countryCode] || null;

// Get country code from subdomain
export const getCountryFromSubdomain = (hostname) => {
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    if (subdomain !== 'www' && SUBDOMAIN_TO_COUNTRY[subdomain]) {
      return SUBDOMAIN_TO_COUNTRY[subdomain];
    }
  }
  return null;
};
